import React, { useRef } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Flag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Sheet, SheetDescription, SheetOverlay, SheetPortal, SheetTitle } from 'components/ui/sheet';
import { PaletteDrawer, type PaletteDrawerProps } from './PaletteDrawer';

export interface PaletteSheetProps extends Omit<PaletteDrawerProps, 'variant'> {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onReviewAndSubmit: () => void;
    isSubmitting: boolean;
}

/**
 * Phone question palette: a bottom sheet with a grab handle (no close button;
 * Escape, the scrim and picking a question close it) and the ink
 * "Yakunlash" action. Built on the shared Sheet scrim.
 */
export function PaletteSheet({ open, onOpenChange, onReviewAndSubmit, isSubmitting, ...palette }: PaletteSheetProps) {
    const { t } = useTranslation();
    const contentRef = useRef<HTMLDivElement>(null);
    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetPortal>
                <SheetOverlay />
                <DialogPrimitive.Content
                    ref={contentRef}
                    data-surface="exam"
                    // Land on the current question's tile rather than the first one.
                    onOpenAutoFocus={(e) => {
                        const current = contentRef.current?.querySelector<HTMLElement>('[aria-current="true"]');
                        if (current) {
                            e.preventDefault();
                            current.focus();
                        }
                    }}
                    className="fixed inset-x-0 bottom-0 z-50 mx-auto flex max-h-[85dvh] w-full max-w-[560px] flex-col rounded-t-xl bg-card px-4 pb-[max(28px,env(safe-area-inset-bottom))] pt-2 text-foreground shadow-[0_-14px_44px_rgba(17,24,39,0.24)] outline-none data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom data-[state=closed]:duration-300 data-[state=open]:duration-500"
                >
                    <span aria-hidden className="mx-auto block h-1 w-10 shrink-0 rounded-full bg-foreground/20" />
                    <div className="flex items-center justify-between gap-3 pb-3 pt-3.5 leading-tight">
                        <SheetTitle className="text-[17px] font-bold">{t('questions')}</SheetTitle>
                        <span className="text-[13px] text-muted-foreground">
                            <span className="font-semibold tabular-nums text-foreground">
                                {palette.answeredCount}/{palette.totalQuestions}
                            </span>{' '}
                            {t('answered')}
                        </span>
                    </div>
                    <SheetDescription className="sr-only">{t('question_navigator')}</SheetDescription>
                    {/* Side padding keeps the tiles' focus rings inside the scroll area. */}
                    <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
                        <PaletteDrawer {...palette} variant="sheet" />
                    </div>
                    <Button
                        type="button"
                        variant="ink"
                        className="mt-4 h-12 w-full shrink-0 gap-1.5"
                        onClick={onReviewAndSubmit}
                        disabled={isSubmitting}
                    >
                        <Flag className="h-4 w-4" aria-hidden />
                        {t('review_and_submit')}
                    </Button>
                </DialogPrimitive.Content>
            </SheetPortal>
        </Sheet>
    );
}

export default PaletteSheet;
